import React, { PureComponent } from 'react';
// redux
import { withFormik } from 'formik';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { updateRecoveryPlan } from '../../../redux/reducers/recovery';
// components
import Icon from '../../../components/Icon/Icon';
import Preloader from '../../../components/Preloader/Preloader';
import Switch from 'rc-switch';
import Input from '../../../components/Input/Input';
import FieldError from '../../../components/Form/FieldError';
import ProgressGoalLine from './ProgressGoalLine';
// utils
import isEmpty from 'lodash.isempty';
import {
  getValuesFromGoals,
  prepareGoalsToSubmit,
  getGoalsValidation,
} from '../utils';
import { toastResponseErrors } from '../../../utils/response';
// shapes
import goalsShape from '../../../shapes/goals';
// styles
import styles from './Goals.mod.css';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

class Goals extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      editMode: false,
      items: this.getItems(props.values),
    };
  }

  componentWillReceiveProps(nextProps) {
    const { values } = nextProps;
    const { items } = this.state;
    if (
      (!nextProps.isRecoveryUpdating && this.props.isRecoveryUpdating) ||
      !items
    ) {
      this.setState({
        editingId: null,
        items: this.getItems(values),
      });
    }
  }

  getItems = (values = {}) => {
    return Object.keys(values).reduce((addedItems, id) => {
      addedItems.push({
        id: id,
        isAdded: !!values[id].value,
      });
      return addedItems;
    }, []);
  };

  toggleEditMode = () => {
    const { initialValues, resetForm } = this.props;
    const { editMode, editingId } = this.state;

    if (editMode) {
      resetForm();
      if (initialValues[editingId] && !initialValues[editingId].value)
        this.deleteItem({ id: editingId });
    }

    this.setState({
      editMode: !editMode,
      editingId: null,
    });
  };

  editItemOn = id => () => {
    const { resetForm } = this.props;
    resetForm();
    this.setState({
      editingId: id,
    });
  };

  editItemOff = ({ event, id }) => {
    const { resetForm, initialValues } = this.props;
    resetForm();

    if (!initialValues[id].value) {
      this.deleteItem({ event, id });
    }

    this.setState({
      editingId: null,
    });
  };

  addNewItem = event => {
    const { items, editingId } = this.state;
    const { setFieldValue, values, initialValues, handleSubmit } = this.props;

    if (
      editingId &&
      (values[editingId].value !== initialValues[editingId].value ||
        values[editingId].deadline !== initialValues[editingId].deadline ||
        values[editingId].award !== initialValues[editingId].award)
    ) {
      handleSubmit(event);
    }

    const newItem = items.find(item => !item.isAdded);
    const newItems = items.filter(item => item.id !== newItem.id);

    newItems.push({ id: newItem.id, isAdded: true });
    setFieldValue(newItem.id, {
      ...values[newItem.id],
      value: '',
      start: new Date().toISOString(),
      award: '',
    });

    this.setState({
      items: newItems,
      editingId: newItem.id,
      editMode: true,
    });
  };

  deleteItem = ({ event, id }) => {
    const { setFieldValue, handleSubmit, values } = this.props;
    const { items } = this.state;
    event && event.persist();

    const newItems = items.map(item =>
      item.id === id ? { id, isAdded: false } : item
    );

    setFieldValue(id, {
      ...values[id],
      deadline: null,
      award: null,
      value: null,
    });

    this.setState(
      () => ({
        items: newItems,
      }),
      () => {
        event && handleSubmit(event);
      }
    );
  };

  handleValueChange = e => {
    const { editingId } = this.state;
    const { setFieldValue, values } = this.props;
    setFieldValue(editingId, { ...values[editingId], value: e.target.value });
  };

  handleAwardChange = e => {
    const { editingId } = this.state;
    const { setFieldValue, values } = this.props;
    setFieldValue(editingId, {
      ...values[editingId],
      award: e.target.value || null,
    });
  };

  handleDeadlineChange = e => {
    const { editingId } = this.state;
    const { setFieldValue, values } = this.props;
    setFieldValue(editingId, {
      ...values[editingId],
      deadline: e.target.value || null,
    });
  };

  renderAddButton = () => {
    const { items, editingId } = this.state;
    const {
      userColor,
      errors,
      isRecoveryUpdating,
      values,
      initialValues,
    } = this.props;

    const hasValidationErrors = !isEmpty(errors);

    const canAdd =
      items.some(item => !item.isAdded) &&
      Object.keys(values).every(
        key => values[key].value === null || values[key].value
      );

    const disabled =
      isRecoveryUpdating ||
      hasValidationErrors ||
      (editingId &&
        (values[editingId].value === initialValues[editingId].value ||
          values[editingId].deadline === initialValues[editingId].deadline ||
          values[editingId].award === initialValues[editingId].award));

    return canAdd ? (
      <button
        className={cx('add-item', {
          disabled,
        })}
        onClick={this.addNewItem}
        style={{ color: userColor }}
      >
        <Icon
          type="add"
          className={cx('add-item-icon')}
          style={{ backgroundColor: userColor }}
        />
        Add new
      </button>
    ) : null;
  };

  renderActionButtons = id => {
    const { userColor, isRecoveryUpdating } = this.props;
    const buttonStyle = { backgroundColor: userColor };
    return (
      <div className={cx('action-buttons')}>
        <button
          className={cx('action-button', 'plain', 'hint--top', {
            disabled: isRecoveryUpdating,
          })}
          onClick={this.editItemOn(id)}
          data-hint="Edit"
        >
          <Icon type="pen" className={cx('icon')} style={buttonStyle} />
        </button>
        <button
          className={cx('action-button', 'plain', 'hint--top', {
            disabled: isRecoveryUpdating,
          })}
          onClick={event => this.deleteItem({ event, id })}
          data-hint="Delete"
        >
          <Icon type="delete" className={cx('icon')} style={buttonStyle} />
        </button>
      </div>
    );
  };

  renderEditingButtons = id => {
    const { isRecoveryUpdating, handleSubmit } = this.props;
    return (
      <div className={cx('action-buttons')}>
        <button
          className={cx('action-button', 'save', 'hint--top', {
            disabled: isRecoveryUpdating,
          })}
          onClick={handleSubmit}
          data-hint="Update"
        >
          <Icon type="check" className={cx('icon')} />
        </button>
        <button
          className={cx('action-button', 'cancel', 'hint--top', {
            disabled: isRecoveryUpdating,
          })}
          onClick={event => this.editItemOff({ event, id })}
          data-hint="Reset"
        >
          <Icon type="close" className={cx('icon')} />
        </button>
      </div>
    );
  };

  render() {
    const { userColor, values, errors, role, isSubmitting } = this.props;
    const { editMode, editingId, items } = this.state;

    const isClient = role === 'client';
    const hasItems = isClient && !isEmpty(items);
    const switchStyle = editMode && {
      backgroundColor: userColor,
      borderColor: userColor,
    };

    return (
      <div className={cx('wrapper')}>
        <div className={cx('title-row')}>
          <Preloader custom isActive={isSubmitting} />
          <h3>Goals</h3>
          {hasItems && (
            <div className={cx('edit-mode-toggle')}>
              <span className="mr-5">EDIT</span>
              <Switch
                checked={editMode}
                onChange={this.toggleEditMode}
                className={cx('switcher')}
                style={switchStyle || {}}
              />
            </div>
          )}
        </div>
        <ul className={cx('editable-list')}>
          {items.map(({ id, isAdded }) => {
            const isEditing = editingId === id;
            const buttons = isEditing
              ? this.renderEditingButtons(id)
              : this.renderActionButtons(id);

            const valueError = errors[id] && errors[id].value;
            const deadlineError = errors[id] && errors[id].deadline;
            const awardError = errors[id] && errors[id].award;

            const showAward = values[id].award && !isEditing && !editMode;

            return isAdded ? (
              <li className={cx('editable-row')} key={id}>
                <div className={cx('item-text', { collapsed: true })}>
                  {isEditing ? (
                    <div>
                      <div className={cx('edit-container')}>
                        <div className={cx('goal-value')}>
                          <Input
                            color={userColor}
                            name={`item-${id}`}
                            className={cx('input')}
                            placeholder={'Type a personal goal'}
                            value={values[id].value}
                            invalid={valueError}
                            disabled={isSubmitting}
                            onChange={this.handleValueChange}
                          />
                          <FieldError error={valueError} touched />
                        </div>
                        <div className={cx('goal-value')}>
                          <Input
                            type="number"
                            className={cx('input')}
                            color={userColor}
                            invalid={deadlineError}
                            placeholder="Please, type days amount"
                            value={values[id].deadline}
                            disabled={isSubmitting}
                            onChange={this.handleDeadlineChange}
                          />
                          <FieldError error={deadlineError} touched />
                        </div>
                        <div className={cx('goal-award')}>
                          <Input
                            color={userColor}
                            name={`award`}
                            className={cx('input')}
                            placeholder={'Award'}
                            value={values[id].award}
                            invalid={awardError}
                            disabled={isSubmitting}
                            onChange={this.handleAwardChange}
                          />
                          <FieldError error={awardError} touched />
                        </div>
                      </div>
                      <FieldError errors={errors[id]} touched />
                    </div>
                  ) : (
                    <div className={cx('goal-info')}>
                      <span>{values[id].value}</span>
                      {!editMode ? (
                        <ProgressGoalLine goal={values[id]} color={userColor} />
                      ) : (
                        <>
                          <span
                            className={cx('deadline', {
                              hidden: !values[id].deadline,
                            })}
                          >
                            <span>Amount of days for achieving the goal: </span>
                            {values[id].deadline}
                          </span>
                          <span
                            className={cx('deadline', {
                              hidden: !values[id].award,
                            })}
                          >
                            <span>Award for achieving the goal: </span>
                            {values[id].award}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className={cx('actions', { editMode })}>{buttons}</div>
                <div
                  className={cx('award-svg', {
                    showAward,
                    'hint--bottom': showAward,
                  })}
                  data-hint={values[id].award}
                >
                  <Icon type="award" className={cx('icon')} color={userColor} />
                </div>
              </li>
            ) : null;
          })}
        </ul>
        {isClient && this.renderAddButton()}
      </div>
    );
  }
}

Goals.propTypes = goalsShape;

const enhance = compose(
  connect(
    state => ({
      role: state.auth.user.role,
      goals: state.recovery.goals,
      isRecoveryUpdating: state.recovery.isRecoveryUpdating,
    }),
    { updateRecoveryPlan }
  ),
  withFormik({
    enableReinitialize: true,
    validateOnChange: false,
    mapPropsToValues: ({ goals }) => getValuesFromGoals(goals),
    validationSchema: ({ goals }) => getGoalsValidation(goals),
    handleSubmit: (
      values,
      { setSubmitting, props: { goals, updateRecoveryPlan } }
    ) => {
      setSubmitting(true);

      updateRecoveryPlan({
        field: 'my_goal',
        data: {
          id: goals.input_item_id,
          question_set: {
            connected_questions: prepareGoalsToSubmit(values),
          },
        },
      })
        .then(() => setSubmitting(false))
        .catch(error => {
          setSubmitting(false);
          toastResponseErrors(error);
        });
    },
  })
);

export default enhance(Goals);



// WEBPACK FOOTER //
// ./src/containers/RecoveryPlan/components/Goals.jsx